/**
 * Cloudinary URL-based image & video optimization utilities.
 * Inserts transforms (resize, quality, format) into Cloudinary URLs
 * without requiring the SDK.
 */

const IMAGE_UPLOAD_SEGMENT = '/image/upload/';
const VIDEO_UPLOAD_SEGMENT = '/video/upload/';
const UPLOAD_SEGMENT = IMAGE_UPLOAD_SEGMENT;

/**
 * Returns a Cloudinary URL with width-based scaling + auto quality/format.
 * No-ops for non-Cloudinary URLs or URLs that already contain transforms.
 */
export function cloudinaryUrl(url: string | undefined, width: number): string {
  if (!url) return '';
  if (!url.includes(UPLOAD_SEGMENT)) return url;

  // Already has transforms (e.g. c_scale, c_fill, w_, q_)
  const afterUpload = url.split(UPLOAD_SEGMENT)[1] || '';
  if (/^[a-z]_/.test(afterUpload)) return url;

  const transforms = `c_scale,w_${width},q_auto,f_auto`;
  return url.replace(UPLOAD_SEGMENT, `${UPLOAD_SEGMENT}${transforms}/`);
}

/**
 * Generates a srcSet string for responsive `<img>` elements.
 * Example: cloudinarySrcSet(url, [400, 800]) → "...w_400... 400w, ...w_800... 800w"
 */
export function cloudinarySrcSet(url: string | undefined, widths: number[]): string {
  if (!url || !url.includes(UPLOAD_SEGMENT)) return '';
  return widths.map(w => `${cloudinaryUrl(url, w)} ${w}w`).join(', ');
}

/**
 * Returns a Cloudinary URL optimized for OG/social previews.
 * Uses c_fill for cropping, f_jpg (not f_auto) because some crawlers don't support WebP.
 */
export function cloudinaryOgImage(url: string | undefined, width = 1200, height = 1200): string {
  if (!url) return '';
  if (!url.includes(UPLOAD_SEGMENT)) return url;

  const afterUpload = url.split(UPLOAD_SEGMENT)[1] || '';
  if (/^[a-z]_/.test(afterUpload)) return url;

  const transforms = `c_fill,w_${width},h_${height},q_auto,f_jpg`;
  return url.replace(UPLOAD_SEGMENT, `${UPLOAD_SEGMENT}${transforms}/`);
}

/**
 * Returns a Cloudinary video URL with auto quality/format.
 * Ensures MOV and other non-web formats get converted to MP4/WebM for browser playback.
 * No-ops for non-Cloudinary URLs or URLs that already contain transforms.
 */
export function cloudinaryVideoUrl(url: string | undefined): string {
  if (!url) return '';
  if (!url.includes(VIDEO_UPLOAD_SEGMENT)) return url;

  const afterUpload = url.split(VIDEO_UPLOAD_SEGMENT)[1] || '';
  if (/^[a-z]_/.test(afterUpload)) return url;

  const transforms = 'q_auto,f_auto';
  return url.replace(VIDEO_UPLOAD_SEGMENT, `${VIDEO_UPLOAD_SEGMENT}${transforms}/`);
}
